import React from "react";

import { GetStaticProps } from "next";
import {
  ATypeCardView,
  ATypeNationalMeritHead,
  BTypeBreadCrumb,
  FullWidthTwinButton,
  RichSearch,
  call,
  nationalMeritFaqRichSearch,
  ATypeNationalMeritSummary,
  GOI_WEB_URL,
  customEvent,
} from "goi_common";
import crematorium from "goi_common/src/constants/hwajang_detail.json";
import facilities from "goi_common/src/constants/facilities_detail.json";
import parlors from "goi_common/src/constants/parlors_detail.json";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos, bestParlorIds } = parlorRegion;

export const getStaticProps: GetStaticProps = async () => {
  const regionCrematoriums = crematorium.filter((p) =>
    targetSidos.includes(String(p.sido))
  );
  const regionPublicFacilities = facilities.filter(
    (f) => targetSidos.includes(f.sido) && f.is_public
  );
  const regionBestParlors = parlors.filter((p) => bestParlorIds.includes(p.id));
  const description =
    "대구경북 지역 국가유공자 장례 혜택 정보. 대구경북 지역 국가유공자 혜택 적용 가능한 장례식장. 대구경북 지역 국가유공자 무료 화장시설. 대구경북 지역 국가유공자 우선 안치 공설 장지. ";

  return {
    props: {
      regionCrematoriums,
      regionPublicFacilities,
      regionBestParlors,
      pageDescription: description,
      pageKeywords: `국가유공자 장례 준비, 대구 국가유공자 장례 혜택, 경북 국가유공자 장례 혜택, 대구경북 국가유공자 장례 해택, 국가유공자 장례 비용`,
      pageOgTitle: `국가유공자 장례정보(대구경북) | 대구경북장례정보포털`,
      pageOgDescription: description,
      pageOgImage: "",
    },
  };
};

export default function index({
  regionCrematoriums,
  regionPublicFacilities,
  regionBestParlors,
  pageOgTitle,
}: {
  regionCrematoriums: Array<any>;
  regionPublicFacilities: Array<any>;
  regionBestParlors: Array<any>;
  pageOgTitle: string;
}) {
  const faqList = nationalMeritFaqRichSearch;
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        <RichSearch data={faqList} />
      </Head>
      <BTypeBreadCrumb
        firstItem={{
          text: `대구 경북 국가유공자 장례헬프센터`,
          href: `/national-merit/`,
          event: {
            domain: "dggb",
            path: "nat_merit",
            trigger_and_target: "br_crumb_nat_merit",
          },
        }}
        goiHomeEvent={{
          domain: "dggb",
          path: "nat_merit",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ATypeNationalMeritHead titleSido="대구 · 경북" mainColor={regionColor} />
      <Stack width="100%" padding="20px" alignItems="center" gap="10px">
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "dggb",
                path: "nat_merit",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "dggb",
                path: "nat_merit",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
        <Box className="subtitle_14_b">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <ATypeNationalMeritSummary mainColor={regionColor} />
      </Stack>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <h3 className="button_16_sb">대구 · 경북 지역 장례식장</h3>
        {regionBestParlors.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`https://www.goifuneral.co.kr/match/parlor-request/${res.companyname}/`}
            descriptions={[
              `빈소 평균 비용 : ${Number(
                res.binso_average_charge
              ).toLocaleString()}원`,
            ]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            arrowText="혜택 알아보기"
            event={{
              domain: "dggb",
              path: "nat_merit",
              trigger_and_target: "uv_prlr_detail",
            }}
          />
        ))}
      </Stack>

      <Stack
        style={{ background: regionColor.c100 }}
        padding="40px 20px"
        marginBottom="40px"
        gap="8px"
      >
        <h3 className="button_16_sb">대구 · 경북 지역 화장장</h3>
        {regionCrematoriums.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`/national-merit/crematorium/${res.companyname}/`}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "dggb",
              path: "nat_merit",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <h3 className="button_16_sb">대구 · 경북 지역 공설 장지</h3>
        {regionPublicFacilities.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`/national-merit/public-facility/${res.companyname}/`}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "dggb",
              path: "nat_merit",
              trigger_and_target: "uv_facility_detail",
            }}
          />
        ))}
      </Stack>
    </>
  );
}
